import {
  AnalyticsEvent,
  ButtonComponentV2,
  CompleteTabapayAuthTransferInput,
  CompleteTabapayAuthTransferResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import { logTransactionAuthEvent } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useState } from 'react';
import CompleteTabapayAuthTransfer from 'services/graphql/mutations/CompleteTabapayAuthTransfer';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

const useCompleteTabapayTransferAuth = () => {
  const [result, executeMutation] = useMutation<Mutation>(
    CompleteTabapayAuthTransfer,
  );
  const { segmentClient } = useAnalyticsConfiguration();

  const [errorScreen, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { fetching: mutationFetching, error: mutationError } = result;
  const completeTabapayTransactionAuth = async (
    input: CompleteTabapayAuthTransferInput,
    flowName?: string,
  ) => {
    try {
      const { data } = await executeMutation({ input });
      if (!data?.completeTabapayAuthTransfer) {
        throw new Error('Mutation did not return data');
      } else if (
        data?.completeTabapayAuthTransfer?.__typename ===
        CompleteTabapayAuthTransferResultTypenames.CompleteTabapayAuthTransferSuccess
      ) {
        logTransactionAuthEvent(
          'Complete Transaction Auth',
          'Success',
          segmentClient,
          flowName,
        );
        return data.completeTabapayAuthTransfer;
      } else if (
        data?.completeTabapayAuthTransfer?.__typename ===
        CompleteTabapayAuthTransferResultTypenames.TabaPayCustomError
      ) {
        const customErrorScreen = getTabapayErrorScreen(
          data.completeTabapayAuthTransfer,
        );
        logTransactionAuthEvent(
          'Complete Transaction Auth',
          'Tabapay Custom Error',
          segmentClient,
          flowName,
        );
        if (customErrorScreen) {
          setErrorScreen(customErrorScreen);
        }
      } else if (
        data?.completeTabapayAuthTransfer?.__typename ===
        CompleteTabapayAuthTransferResultTypenames.CompleteTabapayAuthTransferError
      ) {
        logTransactionAuthEvent(
          'Complete Transaction Auth',
          'Generic_Error',
          segmentClient,
          flowName,
        );
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: data.completeTabapayAuthTransfer.title,
          messageBody: data.completeTabapayAuthTransfer.message,
        });
      }
    } catch (error) {
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    }
  };

  return {
    completeTabapayTransactionAuth,
    isCompleteAuthLoading: mutationFetching,
    completeAuthErrorScreen: errorScreen,
    error: mutationError,
  };
};

export default useCompleteTabapayTransferAuth;
